import { useCallback, useEffect, useMemo, useState } from 'react';
import './styles/fit-live.css';
import './styles/fit-live-public.css';
import { fitLiveApi, mapClassToCard } from './api';
import ImportExcelPanel from './ImportExcelPanel';
import FitLiveClassDetail from './FitLiveClassDetail';
import { DISCIPLINES, WEEKDAY_LABELS } from './constants';
import {
  classToPreview,
  creatorToTrainer,
  fetchPublicDirectory,
  scheduleFromClasses,
  waUrl,
  weekColumns,
} from './creatorDirectory';
import { FitLiveUsersAdmin } from './FitLiveAdminSurfaces';
import FitLiveSpendConfirmModal from './FitLiveSpendConfirmModal';

function pct(n) {
  return `${Math.round(Number(n || 0) * 100)}%`;
}

function ClassCard({
  item, cta = 'Reservar', live = false, onAction, disabled = false, hint = '', badge = '',
}) {
  return (
    <article className="fl-card">
      <div className="fl-card-media">
        <img src={item.image} alt={item.title} loading="lazy" />
        {live ? <span className="fl-live-badge">En vivo</span> : null}
        {badge ? <span className="fl-live-badge" style={{ left: 8, right: 'auto' }}>{badge}</span> : null}
      </div>
      <div className="fl-card-body">
        <div className="fl-level">{item.level}</div>
        <h3 className="fl-card-title">{item.title}</h3>
        <p className="fl-card-meta">
          {item.coach}
          {item.focus ? ` · ${item.focus}` : ''}
          {item.when ? ` · ${item.when}` : ''}
          {item.duration ? ` · ${item.duration} min` : ''}
        </p>
        {hint ? <p className="fl-note" style={{ margin: '0.35rem 0 0' }}>{hint}</p> : null}
        <div className="fl-card-foot">
          <span className="fl-tokens">
            <em>{item.tokens}</em>
            {' '}
            tokens
          </span>
          <button
            type="button"
            className="fl-cta fl-cta-accent"
            disabled={disabled}
            title={hint || cta}
            onClick={() => onAction?.(item)}
          >
            {cta}
          </button>
        </div>
      </div>
    </article>
  );
}

/** Qué falta para publicar (mismo criterio que el backend). */
function publishBlocker(row, profileZoom = '') {
  if (!row || row.status === 'published') return null;
  const tipo = String(row.tipo || 'vivo').toLowerCase();
  if (tipo === 'replay') {
    return String(row.video_url || '').trim() ? null : 'Falta URL de video (replay)';
  }
  if (!String(row.fecha || '').trim() || !String(row.hora || '').trim()) {
    return 'Falta fecha y hora';
  }
  const zoom = String(row.zoom_url || profileZoom || '').trim();
  if (!zoom) return 'Falta Zoom: guárdalo en Perfil creator (arriba) y vuelve a publicar';
  return null;
}

function fitLiveUserMsg(code, fallback) {
  const map = {
    insufficient_tokens: 'Sin tokens disponibles. Compra un pack para continuar.',
    class_full: 'La clase ya no tiene cupo.',
    class_not_found: 'Clase no disponible.',
    use_replay_endpoint: 'Esta es una clase grabada: usa «Ver replay».',
    replay_not_found: 'Replay no disponible.',
    not_a_replay: 'Esta clase no tiene video de grabación.',
    video_required: 'El creator aún no cargó la URL del video.',
  };
  return map[code] || fallback || code || 'Error';
}

function classSpendTokens(item) {
  if (!item) return 0;
  if (item.freeReplay === true || item.free_replay === true) return 0;
  const n = Number(item.tokens ?? item.price_tokens);
  return Number.isFinite(n) && n > 0 ? n : 0;
}

async function reserveClass(item, { setBusy, setMsg, setWallet, refresh }) {
  setBusy(true);
  setMsg('');
  try {
    const res = await fitLiveApi.reserve(item.id);
    setMsg(res.deduped ? 'Ya tenías reserva' : `Reservado · hold ${res.reservation?.tokens_held || 0} tokens`);
    const w = await fitLiveApi.walletMe().catch(() => null);
    if (w?.wallet) setWallet(w.wallet);
    await refresh?.();
  } catch (err) {
    const code = err.payload?.error || err.message;
    setMsg(fitLiveUserMsg(code, err.message || 'Error al reservar'));
    window.scrollTo({ top: 0, behavior: 'smooth' });
  } finally {
    setBusy(false);
  }
}

function WalletBar({ wallet, packages, onBuy, busy, wompi, allowTestPurchase = false }) {
  const wompiReady = Boolean(wompi?.enabled && wompi?.permalink_url);
  const packs = Array.isArray(packages) ? packages : [];
  const canBuy = (wompiReady || allowTestPurchase) && packs.length > 0;
  return (
    <section id="fit-tokens-buy" data-testid="fit-tokens-buy">
      <div className="fl-section-head">
        <div>
          <h2>FitTokens</h2>
          <p>
            {wompiReady
              ? `Compra con Wompi (${wompi.mode})`
              : allowTestPurchase
                ? 'Wompi no configurado — compra prueba solo para super_admin'
                : 'Saldo actual. La compra se habilita cuando Wompi esté configurado en Maestro.'}
          </p>
        </div>
        <span className="fl-banner-phase">
          Saldo
          {' '}
          {wallet?.balance ?? 0}
        </span>
      </div>
      {canBuy ? (
        <>
          <p className="fl-note" style={{ marginBottom: '0.65rem' }}>
            {wompiReady ? 'Comprar tokens — elige un pack:' : 'Compra prueba (no es pago real):'}
          </p>
          <div className="fl-actions">
            {packs.map((p) => (
              <button
                key={p.id}
                type="button"
                className="fl-btn fl-btn-primary"
                disabled={busy}
                onClick={() => onBuy(p.id)}
                title="Comprar FitTokens"
              >
                Comprar
                {' '}
                {p.name}
                {' · '}
                {p.tokens}
                {p.bonus_tokens ? `+${p.bonus_tokens}` : ''}
                {' tok'}
                {p.price_cop ? ` · $${Number(p.price_cop).toLocaleString('es-CO')}` : ''}
              </button>
            ))}
            {wompiReady ? (
              <p className="fl-note" style={{ margin: 0 }}>
                Usa un botón de pack (arriba): registra cuántos tokens vas a comprar y abre Wompi con esa referencia.
              </p>
            ) : null}
          </div>
        </>
      ) : (
        <p className="fl-note">
          {packs.length === 0
            ? 'No hay packs configurados en el maestro.'
            : 'Compra de tokens no disponible aún (activa Wompi + permalink en Maestro Fit Live).'}
        </p>
      )}
    </section>
  );
}

function MyAgenda({ reservations, busy, onCancel, onRate, onTip, onOpenZoom }) {
  if (!reservations?.length) return null;
  return (
    <section>
      <div className="fl-section-head">
        <div>
          <h2>Mi agenda</h2>
          <p>Reservas activas · al entrar a Zoom se registra tu asistencia</p>
        </div>
      </div>
      <div className="fl-agenda-list">
        {reservations.map((r) => (
          <div key={r.id} className="fl-agenda-row">
            <div className="fl-agenda-copy">
              <strong>{r.class_title || r.class_id}</strong>
              <span>
                {r.status === 'attended' ? 'Asistió' : r.status === 'held' || r.status === 'confirmed' ? 'Reservada' : r.status}
                {r.starts_at ? ` · ${new Date(r.starts_at).toLocaleString('es-CO')}` : ''}
                {' · '}
                {r.tokens_held}
                {' tok'}
                {!r.zoom_url && ['held', 'confirmed'].includes(r.status) ? ' · Zoom pendiente del creator' : ''}
              </span>
            </div>
            <div className="fl-actions">
              {r.zoom_url && ['held', 'confirmed', 'attended'].includes(r.status) ? (
                <button type="button" className="fl-btn fl-btn-primary" disabled={busy} onClick={() => onOpenZoom(r)}>
                  Entrar Zoom
                </button>
              ) : null}
              {['held', 'confirmed'].includes(r.status) ? (
                <button type="button" className="fl-btn" disabled={busy} onClick={() => onCancel(r)}>
                  Cancelar
                </button>
              ) : null}
              {r.status === 'attended' ? (
                <>
                  <button type="button" className="fl-btn" disabled={busy} onClick={() => onRate(r)}>
                    Valorar
                  </button>
                  <button type="button" className="fl-btn fl-btn-primary" disabled={busy} onClick={() => onTip(r)}>
                    Tip 2 tok
                  </button>
                </>
              ) : null}
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

function AthleteHome({
  trainerId, setTrainerId, creators, published, wallet, packages, onBuy, busy, wompi,
  allowTestPurchase = false,
  onReserve, onWatchReplay, reservations, onCancel, onRate, onTip, onOpenZoom,
}) {
  const [selectedPreview, setSelectedPreview] = useState(null);

  const trainers = useMemo(() => creators.map(creatorToTrainer), [creators]);
  const trainer = useMemo(
    () => trainers.find((t) => t.id === trainerId) || trainers[0] || null,
    [trainers, trainerId],
  );

  const trainerClasses = useMemo(
    () => (trainer ? published.filter((c) => c.creator_id === trainer.id) : []),
    [published, trainer],
  );
  const schedule = useMemo(() => scheduleFromClasses(trainerClasses), [trainerClasses]);
  const weekDays = useMemo(() => weekColumns(schedule), [schedule]);
  const previews = useMemo(() => trainerClasses.map(classToPreview), [trainerClasses]);

  const liveRows = useMemo(
    () => trainerClasses.filter((c) => String(c.tipo || 'vivo').toLowerCase() !== 'replay'),
    [trainerClasses],
  );
  const replayRows = useMemo(
    () => trainerClasses.filter((c) => String(c.tipo || '').toLowerCase() === 'replay'),
    [trainerClasses],
  );
  const cards = useMemo(() => liveRows.map(mapClassToCard), [liveRows]);

  const hero = cards.slice(0, 2);
  const rest = cards.slice(2);

  const replayCards = replayRows.map(mapClassToCard);

  const openPreview = (pv) => {
    setSelectedPreview(pv);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  if (selectedPreview) {
    return (
      <FitLiveClassDetail
        classItem={selectedPreview}
        trainer={trainer}
        schedule={schedule}
        onBack={() => setSelectedPreview(null)}
        onTryFree={() => {
          const row = trainerClasses.find((c) => c.id === selectedPreview.id) || { id: selectedPreview.id };
          if (row.tipo === 'replay') onWatchReplay(row);
          else onReserve(row);
          setSelectedPreview(null);
        }}
        onLogin={() => setSelectedPreview(null)}
      />
    );
  }

  return (
    <>
      <WalletBar
        wallet={wallet}
        packages={packages}
        onBuy={onBuy}
        busy={busy}
        wompi={wompi}
        allowTestPurchase={allowTestPurchase}
      />
      <MyAgenda
        reservations={reservations}
        busy={busy}
        onCancel={onCancel}
        onRate={onRate}
        onTip={onTip}
        onOpenZoom={onOpenZoom}
      />

      <section>
        <div className="fl-section-head">
          <div>
            <h2>Conoce nuestros entrenadores</h2>
            <p>Elige un entrenador · abre su calendario y explora sus clases.</p>
          </div>
        </div>
        {trainers.length === 0 ? (
          <p className="fl-note">
            Aún no hay creators publicados. El super_admin los da de alta en Fit Live → Usuarios → Creators.
          </p>
        ) : (
          <div className="fl-pub-disciplines" role="tablist" aria-label="Entrenadores">
            {trainers.map((t) => (
              <button
                key={t.id}
                type="button"
                role="tab"
                aria-selected={trainer?.id === t.id}
                className={`fl-pub-disc${trainer?.id === t.id ? ' is-active' : ''}`}
                onClick={() => setTrainerId(t.id)}
              >
                <span className="fl-pub-disc-icon" aria-hidden>
                  <img src={t.logo} alt="" width={72} height={72} />
                </span>
                {t.label}
                {t.isNew ? ' · Nuevo' : ''}
                <p className="fl-pub-disc-caption">{t.caption}</p>
              </button>
            ))}
          </div>
        )}
      </section>

      {trainer ? (
      <>
      <section id="entrenador" className="fl-trainer-panel">
        <div className="fl-trainer-head">
          <img className="fl-trainer-logo" src={trainer.logo} alt={trainer.label} width={88} height={88} />
          <div>
            <p className="fl-trainer-kicker">Entrenador Fit Live</p>
            <h2>{trainer.label}</h2>
            {trainer.bio ? <p className="fl-trainer-bio">{trainer.bio}</p> : null}
            <p className="fl-trainer-spec">{trainer.specialty}</p>
          </div>
        </div>

        <div className="fl-section-head" style={{ marginTop: '1.25rem' }}>
          <div>
            <h3 className="fl-trainer-cal-title">Programación</h3>
            <p>Próximas clases en vivo. Toca una clase para ver el detalle.</p>
          </div>
          <span className="fl-banner-phase">{schedule.length} próximas</span>
        </div>
        <div className="fl-week-cal" role="grid" aria-label="Calendario semanal">
          {weekDays.map((col) => (
            <div key={col.day} className="fl-week-col" role="gridcell">
              <div className="fl-week-day">{WEEKDAY_LABELS[col.day]}</div>
              <div className="fl-week-slots">
                {col.slots.length === 0 ? (
                  <p className="fl-week-empty">—</p>
                ) : col.slots.map((slot) => (
                  <button
                    key={slot.classId}
                    type="button"
                    className="fl-week-slot"
                    onClick={() => {
                      const pv = previews.find((p) => p.id === slot.classId);
                      if (pv) openPreview(pv);
                    }}
                  >
                    <strong>{slot.time}</strong>
                    <span>{slot.title}</span>
                    <em>
                      {slot.duration}
                      {' '}
                      min ·
                      {slot.level}
                      {' · '}
                      {slot.tokens}
                      {' '}
                      tok
                    </em>
                  </button>
                ))}
              </div>
            </div>
          ))}
        </div>
      </section>

      <section id="previews">
        <div className="fl-section-head">
          <div>
            <h2>Explora las clases de {trainer.label}</h2>
            <p>Al hacer clic abres el detalle de la clase.</p>
          </div>
          <span className="fl-banner-phase">{previews.length} clases</span>
        </div>
        <div className="fl-preview-grid">
          {previews.map((pv) => (
            <button
              key={pv.id}
              type="button"
              className="fl-preview-card"
              onClick={() => openPreview(pv)}
            >
              <div className="fl-preview-media">
                <img src={pv.image} alt="" loading="lazy" />
                <span className="fl-preview-play" aria-hidden>▶</span>
              </div>
              <div className="fl-preview-body">
                <p className="fl-preview-level">{pv.level}</p>
                <h3>{pv.title}</h3>
                <p className="fl-preview-meta">
                  {pv.coach}
                  {' · '}
                  {pv.discipline}
                </p>
              </div>
            </button>
          ))}
        </div>
      </section>

      <section>
        <div className="fl-section-head">
          <div>
            <h2>En vivo / publicadas</h2>
            <p>{`Catálogo de ${trainer.label} · al reservar se apartan los tokens`}</p>
          </div>
        </div>
        <div className="fl-grid fl-grid-live">
          {hero.map((item) => (
            <ClassCard key={item.id} item={item} cta="Reservar" live onAction={onReserve} />
          ))}
          {!hero.length ? <p className="fl-note">Sin clases en vivo publicadas para este entrenador todavía.</p> : null}
        </div>
      </section>

      <section>
        <div className="fl-section-head">
          <div>
            <h2>Próximas</h2>
            <p>Coach · nivel · tokens</p>
          </div>
        </div>
        <div className="fl-grid">
          {rest.map((item) => (
            <ClassCard key={item.id} item={item} cta="Reservar" onAction={onReserve} />
          ))}
        </div>
      </section>

      <section>
        <div className="fl-section-head">
          <div>
            <h2>Clases grabadas</h2>
            <p>Replay bajo demanda · gratis o con tokens según el creator</p>
          </div>
        </div>
        <div className="fl-grid">
          {replayCards.map((item) => (
            <ClassCard
              key={item.id}
              item={item}
              cta={item.freeReplay ? 'Ver gratis' : 'Ver replay'}
              badge={item.freeReplay ? 'Gratis' : 'Grabada'}
              onAction={onWatchReplay}
            />
          ))}
          {!replayCards.length ? <p className="fl-note">Este entrenador aún no tiene clases grabadas.</p> : null}
        </div>
      </section>
      </>
      ) : null}
    </>
  );
}

function CreatorStudio({
  mine, onRefresh, busy, setBusy, setMsg, showImport, setShowImport, isPlatformAdmin = false,
}) {
  const [myZoom, setMyZoom] = useState({
    display_name: '',
    bio: '',
    zoom_url: '',
    zoom_passcode: '',
    whatsapp: '',
    payout_bank: '',
    payout_account_number: '',
    payout_account_type: 'ahorros',
    payout_holder_name: '',
    payout_document: '',
  });
  const [earnings, setEarnings] = useState(null);
  const [form, setForm] = useState(() => {
    const d = new Date();
    const fecha = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    return {
      title: '',
      discipline: 'hiit',
      level: 'intermedio',
      duration_min: 45,
      price_tokens: 8,
      free_replay: false,
      focus: '',
      description: '',
      tipo: 'vivo',
      fecha,
      hora: '18:00',
      zoom_url: '',
      video_url: '',
      cupo: 20,
    };
  });
  const [classImageFile, setClassImageFile] = useState(null);
  const [classImagePreview, setClassImagePreview] = useState('');
  const [creatorAvatar, setCreatorAvatar] = useState('');

  useEffect(() => {
    fitLiveApi.me().then((r) => {
      if (r?.creator) {
        setMyZoom({
          display_name: r.creator.display_name || '',
          bio: r.creator.bio || '',
          zoom_url: r.creator.zoom_url || '',
          zoom_passcode: r.creator.zoom_passcode || '',
          whatsapp: r.creator.whatsapp || '',
          payout_bank: r.creator.payout_bank || '',
          payout_account_number: r.creator.payout_account_number || '',
          payout_account_type: r.creator.payout_account_type || 'ahorros',
          payout_holder_name: r.creator.payout_holder_name || '',
          payout_document: r.creator.payout_document || '',
        });
        setCreatorAvatar(r.creator.avatar_url || '');
      }
      if (r?.earnings) setEarnings(r.earnings);
    }).catch(() => {});
  }, []);

  const saveMyZoom = async () => {
    setBusy(true);
    setMsg('');
    try {
      const {
        display_name: displayName,
        whatsapp,
        ...rest
      } = myZoom;
      const payload = String(displayName).trim() ? { ...rest, display_name: displayName } : rest;
      if (isPlatformAdmin) payload.whatsapp = whatsapp;
      const res = await fitLiveApi.creatorMePatch(payload);
      if (res?.creator) {
        setMyZoom((z) => ({
          ...z,
          display_name: res.creator.display_name || z.display_name,
          payout_bank: res.creator.payout_bank || '',
          payout_account_number: res.creator.payout_account_number || '',
          payout_account_type: res.creator.payout_account_type || z.payout_account_type,
          payout_holder_name: res.creator.payout_holder_name || '',
          payout_document: res.creator.payout_document || '',
          ...(isPlatformAdmin ? { whatsapp: res.creator.whatsapp || '' } : {}),
        }));
      }
      setMsg('Tu perfil de entrenador quedó guardado');
    } catch (err) {
      setMsg(err.message || 'Error al guardar el perfil');
    } finally {
      setBusy(false);
    }
  };

  const onCreate = async (e) => {
    e.preventDefault();
    setBusy(true);
    setMsg('');
    try {
      const videoUrl = String(form.video_url || '').trim();
      const asReplay = form.tipo === 'replay' || form.free_replay || Boolean(videoUrl);
      if (asReplay && !videoUrl) {
        setMsg('Para clase grabada indica la URL del video (campo Video de clase grabada).');
        setBusy(false);
        return;
      }
      const payload = {
        ...form,
        tipo: asReplay ? 'replay' : form.tipo,
        video_url: videoUrl,
        free_replay: Boolean(form.free_replay),
        price_tokens: form.free_replay ? 0 : Number(form.price_tokens) || 8,
      };
      const res = await fitLiveApi.createClass(payload);
      const created = res?.class;
      if (created?.id && classImageFile) {
        await fitLiveApi.classImage(created.id, classImageFile);
      }
      setForm((f) => ({
        ...f,
        title: '',
        focus: '',
        description: '',
        video_url: '',
        free_replay: false,
        tipo: 'vivo',
        price_tokens: 8,
      }));
      setClassImageFile(null);
      if (classImagePreview) URL.revokeObjectURL(classImagePreview);
      setClassImagePreview('');
      const kind = created?.tipo === 'replay'
        ? (created.free_replay ? 'Clase grabada gratis' : 'Clase grabada')
        : 'Clase en vivo';
      setMsg(classImageFile
        ? `${kind} creada con imagen de tarjeta.`
        : `${kind} creada. Sin imagen propia: se usa el logo del creator en la tarjeta.`);
      await onRefresh();
    } catch (err) {
      setMsg(err.message || 'Error al crear');
    } finally {
      setBusy(false);
    }
  };

  const [catalogMsg, setCatalogMsg] = useState('');

  const togglePublish = async (row) => {
    setBusy(true);
    setMsg('');
    setCatalogMsg('');
    const blocker = row.status === 'published' ? null : publishBlocker(row, myZoom.zoom_url);
    if (blocker) {
      setCatalogMsg(blocker);
      setMsg(blocker);
      setBusy(false);
      return;
    }
    try {
      if (row.status === 'published') await fitLiveApi.unpublish(row.id);
      else await fitLiveApi.publish(row.id);
      const ok = row.status === 'published' ? 'Clase despublicada' : 'Clase publicada';
      setMsg(ok);
      setCatalogMsg(ok);
      await onRefresh();
    } catch (err) {
      const code = err.payload?.error || err.message;
      const map = {
        schedule_required: 'Para publicar en vivo indica fecha y hora',
        zoom_required: 'Guarda Zoom en Perfil creator (arriba) y pulsa «Guardar mi perfil», luego Publicar',
        video_required: 'Replay requiere URL de video',
        trial_price_cap: 'Precio sobre el tope de trial',
        creator_not_enabled: 'Creator no habilitado',
        not_found: 'No se encontró la clase (¿es de otro creator?)',
        forbidden: 'Sin permiso para publicar esta clase',
      };
      const text = map[code] || code || 'Error al publicar';
      setMsg(text);
      setCatalogMsg(text);
    } finally {
      setBusy(false);
    }
  };

  const publishedCount = mine.filter((c) => c.status === 'published').length;
  const draftCount = mine.filter((c) => c.status === 'draft').length;

  return (
    <>
      <section>
        <div className="fl-section-head">
          <div>
            <h2>Creator Studio</h2>
            <p>Crea · publica · tu catálogo (sin migrar legado)</p>
          </div>
          <span className="fl-banner-phase">
            {publishedCount}
            {' pub · '}
            {draftCount}
            {' draft'}
          </span>
        </div>
        <div className="fl-kpi-grid">
          <div className="fl-kpi"><label>Publicadas</label><strong>{publishedCount}</strong></div>
          <div className="fl-kpi"><label>Borradores</label><strong>{draftCount}</strong></div>
          {earnings ? (
            <>
              <div className="fl-kpi">
                <label>Tokens consumidos (tus clases)</label>
                <strong>{earnings.tokens_attributed || 0}</strong>
              </div>
              <div className="fl-kpi">
                <label>
                  Tu liquidación (
                  {earnings.split_creator_pct || 80}
                  %)
                </label>
                <strong>
                  $
                  {Number(earnings.net_cop || 0).toLocaleString('es-CO')}
                </strong>
              </div>
            </>
          ) : null}
        </div>
        {earnings ? (
          <p className="fl-note">
            Solo ves tu parte (
            {earnings.split_creator_pct || 80}
            %). Valor de liquidación:
            {' '}
            {Number(earnings.settlement_value_cop_per_token || 0).toLocaleString('es-CO')}
            {' COP por token consumido en tus clases.'}
          </p>
        ) : null}
      </section>

      <section>
        <div className="fl-section-head">
          <div>
            <h2>Perfil creator</h2>
            <p>
              Nombre y bio públicos + Zoom/Meet por defecto + cuenta de pago.
              {' '}
              No es marca blanca (eso es Training).
            </p>
          </div>
        </div>
        <div className="fl-form-grid">
          <label className="fl-form-field">
            <span>Nombre público</span>
            <input
              className="fl-field"
              value={myZoom.display_name}
              onChange={(e) => setMyZoom({ ...myZoom, display_name: e.target.value })}
              maxLength={120}
            />
          </label>
          <label className="fl-form-field fl-form-field--wide">
            <span>Bio</span>
            <textarea
              className="fl-field"
              rows={2}
              value={myZoom.bio}
              onChange={(e) => setMyZoom({ ...myZoom, bio: e.target.value })}
              maxLength={1000}
            />
          </label>
          <label className="fl-form-field fl-form-field--wide">
            <span>Zoom URL (obligatorio para publicar clases en vivo)</span>
            <input
              className="fl-field"
              value={myZoom.zoom_url}
              onChange={(e) => setMyZoom({ ...myZoom, zoom_url: e.target.value })}
              placeholder="https://zoom.us/j/…"
            />
          </label>
          <label className="fl-form-field">
            <span>Passcode</span>
            <input
              className="fl-field"
              value={myZoom.zoom_passcode}
              onChange={(e) => setMyZoom({ ...myZoom, zoom_passcode: e.target.value })}
            />
          </label>
          {isPlatformAdmin ? (
            <label className="fl-form-field">
              <span>WhatsApp (solo super_admin)</span>
              <input
                className="fl-field"
                value={myZoom.whatsapp}
                onChange={(e) => setMyZoom({ ...myZoom, whatsapp: e.target.value })}
              />
            </label>
          ) : null}
        </div>

        <div className="fl-section-head" style={{ marginTop: '0.65rem' }}>
          <div>
            <h3 style={{ margin: 0, fontSize: '1.05rem' }}>Cuenta donde se te debe pagar</h3>
            <p className="fl-note" style={{ margin: '0.25rem 0 0' }}>
              Datos para liquidar tu porcentaje (80 %). Solo los usa la plataforma.
            </p>
          </div>
        </div>
        <div className="fl-form-grid">
          <label className="fl-form-field">
            <span>Banco</span>
            <input
              className="fl-field"
              value={myZoom.payout_bank}
              onChange={(e) => setMyZoom({ ...myZoom, payout_bank: e.target.value })}
              placeholder="Bancolombia, Nequi…"
              maxLength={80}
            />
          </label>
          <label className="fl-form-field">
            <span>Número de cuenta</span>
            <input
              className="fl-field"
              value={myZoom.payout_account_number}
              onChange={(e) => setMyZoom({ ...myZoom, payout_account_number: e.target.value })}
              maxLength={40}
            />
          </label>
          <label className="fl-form-field">
            <span>Tipo de cuenta</span>
            <select
              className="fl-field"
              value={myZoom.payout_account_type}
              onChange={(e) => setMyZoom({ ...myZoom, payout_account_type: e.target.value })}
            >
              <option value="ahorros">Ahorros</option>
              <option value="corriente">Corriente</option>
            </select>
          </label>
          <label className="fl-form-field">
            <span>Nombre del titular</span>
            <input
              className="fl-field"
              value={myZoom.payout_holder_name}
              onChange={(e) => setMyZoom({ ...myZoom, payout_holder_name: e.target.value })}
              maxLength={120}
            />
          </label>
          <label className="fl-form-field">
            <span>Cédula</span>
            <input
              className="fl-field"
              value={myZoom.payout_document}
              onChange={(e) => setMyZoom({ ...myZoom, payout_document: e.target.value })}
              placeholder="Número de documento"
              maxLength={32}
            />
          </label>
        </div>
        <div className="fl-actions" style={{ marginTop: 10 }}>
          <button type="button" className="fl-btn fl-btn-primary" disabled={busy} onClick={saveMyZoom}>
            Guardar mi perfil
          </button>
        </div>
      </section>

      <section>
        <div className="fl-section-head">
          <div>
            <h2>Crear clase</h2>
            <p>Manual · o descarga la plantilla Excel, llénala y cárgala aquí</p>
          </div>
        </div>
        <form className="fl-create-form" onSubmit={onCreate}>
          <div className="fl-class-image-box">
            <div className="fl-class-image-preview">
              <img
                src={classImagePreview || creatorAvatar || 'https://images.unsplash.com/photo-1518611012118-696072aa579a?auto=format&fit=crop&w=600&q=70'}
                alt="Vista tarjeta"
              />
              {!classImagePreview ? (
                <span className="fl-class-image-hint">
                  {creatorAvatar ? 'Por defecto: logo del creator' : 'Sin imagen — sube una o usa el logo del perfil'}
                </span>
              ) : null}
            </div>
            <label className="fl-kpi" style={{ flex: 1 }}>
              <span>Imagen de la tarjeta</span>
              <input
                className="fl-field"
                type="file"
                accept="image/png,image/jpeg,image/webp,image/gif"
                onChange={(e) => {
                  const file = e.target.files?.[0] || null;
                  if (classImagePreview) URL.revokeObjectURL(classImagePreview);
                  setClassImageFile(file);
                  setClassImagePreview(file ? URL.createObjectURL(file) : '');
                }}
              />
              <small className="fl-note" style={{ display: 'block', marginTop: 6 }}>
                PNG/JPG máx. 2 MB. Si no subes, la tarjeta usa tu foto/logo de creator.
              </small>
            </label>
          </div>
          <div className="fl-form-grid">
            <label className="fl-form-field">
              <span>Nombre</span>
              <input
                required
                value={form.title}
                onChange={(e) => setForm({ ...form, title: e.target.value })}
                className="fl-field"
              />
            </label>
            <label className="fl-form-field">
              <span>Disciplina</span>
              <select
                value={form.discipline}
                onChange={(e) => setForm({ ...form, discipline: e.target.value })}
                className="fl-field"
              >
                {DISCIPLINES.map((d) => (
                  <option key={d.id} value={d.id}>{d.label}</option>
                ))}
              </select>
            </label>
            <label className="fl-form-field">
              <span>Nivel</span>
              <select
                value={form.level}
                onChange={(e) => setForm({ ...form, level: e.target.value })}
                className="fl-field"
              >
                <option value="inicial">Inicial</option>
                <option value="intermedio">Intermedio</option>
                <option value="avanzado">Avanzado</option>
              </select>
            </label>
            <label className="fl-form-field">
              <span>Duración (min)</span>
              <input
                type="number"
                min={5}
                max={180}
                required
                value={form.duration_min}
                onChange={(e) => setForm({ ...form, duration_min: Number(e.target.value) })}
                className="fl-field"
              />
            </label>
            <label className="fl-form-field">
              <span>Tokens</span>
              <input
                type="number"
                min={1}
                max={500}
                required
                value={form.price_tokens}
                onChange={(e) => setForm({ ...form, price_tokens: Number(e.target.value) })}
                className="fl-field"
              />
            </label>
            <label className="fl-form-field">
              <span>Foco</span>
              <input
                value={form.focus}
                onChange={(e) => setForm({ ...form, focus: e.target.value })}
                className="fl-field"
              />
            </label>
            <label className="fl-form-field">
              <span>Tipo</span>
              <select
                value={form.tipo}
                onChange={(e) => {
                  const tipo = e.target.value;
                  setForm({
                    ...form,
                    tipo,
                    free_replay: tipo === 'replay' ? form.free_replay : false,
                  });
                }}
                className="fl-field"
              >
                <option value="vivo">En vivo</option>
                <option value="replay">Clase grabada (replay)</option>
                <option value="hibrido">Híbrido</option>
              </select>
            </label>
            <label className="fl-form-field">
              <span>Fecha (calendario)</span>
              <input
                type="date"
                required={form.tipo !== 'replay'}
                value={form.fecha}
                onChange={(e) => setForm({ ...form, fecha: e.target.value })}
                className="fl-field"
              />
            </label>
            <label className="fl-form-field">
              <span>Hora</span>
              <input
                type="time"
                required={form.tipo !== 'replay'}
                value={form.hora}
                onChange={(e) => setForm({ ...form, hora: e.target.value })}
                className="fl-field"
              />
            </label>
            <label className="fl-form-field">
              <span>Cupo</span>
              <input
                type="number"
                min={1}
                max={500}
                value={form.cupo}
                onChange={(e) => setForm({ ...form, cupo: Number(e.target.value) })}
                className="fl-field"
              />
            </label>
            <label className="fl-form-field fl-form-field--wide">
              <span>Zoom URL</span>
              <input
                value={form.zoom_url}
                onChange={(e) => setForm({ ...form, zoom_url: e.target.value })}
                placeholder="https://zoom.us/j/…"
                className="fl-field"
              />
            </label>
            <label className="fl-form-field fl-form-field--wide">
              <span>Video de clase grabada (URL)</span>
              <input
                value={form.video_url}
                onChange={(e) => {
                  const video_url = e.target.value;
                  setForm({
                    ...form,
                    video_url,
                    // Al pegar URL de video, la clase pasa a grabada (no en vivo).
                    tipo: String(video_url).trim() ? 'replay' : form.tipo,
                  });
                }}
                placeholder="https://… (Zoom cloud, YouTube, Vimeo…)"
                className="fl-field"
              />
            </label>
            {form.tipo === 'replay' || String(form.video_url || '').trim() ? (
              <label className="fl-form-field fl-form-field--wide" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <input
                  type="checkbox"
                  checked={Boolean(form.free_replay)}
                  onChange={(e) => setForm({
                    ...form,
                    free_replay: e.target.checked,
                    tipo: 'replay',
                    price_tokens: e.target.checked ? 0 : (form.price_tokens || 8),
                  })}
                />
                <span>Replay gratis (todos lo ven sin gastar tokens · se marca «Gratis»)</span>
              </label>
            ) : null}
          </div>
          <div className="fl-actions" style={{ flexWrap: 'wrap' }}>
            <button type="submit" className="fl-btn fl-btn-primary" disabled={busy}>
              Guardar borrador
            </button>
            <button
              type="button"
              className="fl-btn"
              disabled={busy}
              onClick={async () => {
                setBusy(true);
                setMsg('');
                try {
                  await fitLiveApi.downloadTemplate();
                  setMsg('Plantilla Excel descargada. Llénala y usa «Cargar Excel» para subirla.');
                } catch (err) {
                  setMsg(err.message || 'No se pudo descargar la plantilla');
                } finally {
                  setBusy(false);
                }
              }}
            >
              Descargar plantilla Excel
            </button>
            <button type="button" className="fl-btn fl-btn-primary" disabled={busy} onClick={() => setShowImport((v) => !v)}>
              {showImport ? 'Ocultar Excel' : 'Cargar Excel'}
            </button>
          </div>
        </form>
      </section>

      {showImport ? (
        <ImportExcelPanel onDone={onRefresh} setMsg={setMsg} busy={busy} setBusy={setBusy} />
      ) : null}

      <section>
        <div className="fl-section-head">
          <div>
            <h2>Mi catálogo</h2>
            <p>Publicar / despublicar · en vivo requiere Zoom en tu perfil</p>
          </div>
        </div>
        {catalogMsg ? (
          <p className="fl-note" style={{ marginBottom: 12, fontWeight: 600 }} role="status">
            {catalogMsg}
          </p>
        ) : null}
        {!String(myZoom.zoom_url || '').trim() ? (
          <p className="fl-note" style={{ marginBottom: 12 }}>
            Aún no tienes Zoom en el perfil. Guárdalo arriba («Guardar mi perfil») o las clases en vivo no se podrán publicar.
          </p>
        ) : null}
        {mine.length === 0 ? (
          <p className="fl-note">Aún no tienes clases. Crea la primera arriba.</p>
        ) : (
          <div className="fl-grid">
            {mine.map((row) => {
              const card = mapClassToCard(row, { creatorAvatar });
              const blocker = publishBlocker(row, myZoom.zoom_url);
              const isPub = row.status === 'published';
              const isReplay = String(row.tipo || '').toLowerCase() === 'replay';
              const badge = isReplay
                ? `${isPub ? 'Grabada' : 'Borrador'}${row.free_replay || Number(row.price_tokens) === 0 ? ' · Gratis' : ''}`
                : (isPub ? 'Publicada' : 'Borrador');
              return (
                <ClassCard
                  key={row.id}
                  item={card}
                  badge={badge}
                  cta={isPub ? 'Despublicar' : 'Publicar'}
                  hint={blocker || ''}
                  disabled={busy}
                  onAction={() => togglePublish(row)}
                />
              );
            })}
          </div>
        )}
      </section>
    </>
  );
}

function MaestroEcosistema({ busy, setBusy, setMsg }) {
  const [master, setMaster] = useState(null);
  const [settlement, setSettlement] = useState(null);
  const [cloneInfo, setCloneInfo] = useState(null);
  const [payouts, setPayouts] = useState([]);
  const [economy, setEconomy] = useState(null);
  const [notifications, setNotifications] = useState([]);
  const [loaded, setLoaded] = useState(false);

  const load = async () => {
    try {
      const data = await fitLiveApi.masterGet();
      setMaster(data.master);
      setSettlement(data.settlement || null);
      setCloneInfo(data.wompi_clone || null);
      setNotifications(Array.isArray(data.notifications) ? data.notifications : []);
      const [pay, eco] = await Promise.all([
        fitLiveApi.payouts().catch(() => ({ payouts: [] })),
        fitLiveApi.economy().catch(() => null),
      ]);
      setPayouts(pay.payouts || []);
      if (eco?.economy) setEconomy(eco.economy);
      else if (data.economy) setEconomy(data.economy);
      setLoaded(true);
      if (data.wompi_clone?.cloned) {
        setMsg('Permalink Wompi clonado desde Training (copia propia Fit Live)');
      }
    } catch (err) {
      setMsg(err.message || 'No se pudo cargar el maestro');
    }
  };

  const saveEconomy = async (e) => {
    e.preventDefault();
    if (!economy) return;
    setBusy(true);
    setMsg('');
    try {
      const creatorPct = Number(economy.split_creator_pct) || 80;
      const res = await fitLiveApi.patchEconomy({
        sale_price_cop_per_token: Number(economy.sale_price_cop_per_token),
        split_creator_pct: creatorPct,
        split_platform_pct: 100 - creatorPct,
        packages: economy.packages,
        welcome_bonus: economy.welcome_bonus,
      });
      setEconomy(res.economy);
      setMsg('Economía FitTokens guardada (precio · packs · split 80/20)');
      const masterData = await fitLiveApi.masterGet().catch(() => null);
      if (masterData?.settlement) setSettlement(masterData.settlement);
    } catch (err) {
      setMsg(err.message || 'Error al guardar economía');
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const saveMaster = async (e) => {
    e.preventDefault();
    setBusy(true);
    setMsg('');
    try {
      const res = await fitLiveApi.masterPatch({
        brand_name: master.brand_name,
        support_whatsapp: master.support_whatsapp,
        wompi: master.wompi,
        live_policies: master.live_policies,
        legal: master.legal,
        creator_levels: master.creator_levels,
        beta_thresholds: master.beta_thresholds,
      });
      setMaster(res.master);
      setMsg('Maestro guardado');
    } catch (err) {
      setMsg(err.message || 'Error al guardar maestro');
    } finally {
      setBusy(false);
    }
  };

  if (!loaded || !master) {
    return <p className="fl-note">Cargando maestro del ecosistema…</p>;
  }

  return (
    <>
      <section>
        <div className="fl-section-head">
          <div>
            <h2>Maestro ecosistema</h2>
            <p>Aquí defines precio del token, packs Wompi, split 80/20 y liquidación por creator</p>
          </div>
          <span className="fl-banner-phase">Config</span>
        </div>
        <div className="fl-note" style={{ marginTop: 8 }}>
          <strong>Cómo fluyen los FitTokens:</strong>
          {' '}
          1) En esta pestaña fijas el valor COP/token y los packs.
          {' '}
          2) El atleta compra en Marketplace (Wompi) o tú otorgas en Usuarios → Vigencias.
          {' '}
          3) Al asistir/consumir, el creator acumula tokens → liquidación = tokens × precio × 80 %.
          {' '}
          4) Generas payouts abajo y marcas pagado. El creator en Studio solo ve su 80 %.
        </div>
      </section>

      <section>
        <div className="fl-section-head">
          <div>
            <h2>Avisos de compra de tokens</h2>
            <p>
              Cuando un atleta pulsa «Comprar pack», llega aquí. Verifica el pago en Usuarios → Vigencias y acredita tokens.
            </p>
          </div>
          <button type="button" className="fl-btn" disabled={busy} onClick={load}>Actualizar avisos</button>
        </div>
        <div className="fl-table-wrap">
          <table className="fl-table">
            <thead>
              <tr>
                <th>Cuándo</th>
                <th>Tipo</th>
                <th>Detalle</th>
              </tr>
            </thead>
            <tbody>
              {(notifications || [])
                .filter((n) => n.meta?.type === 'purchase_checkout' || String(n.template || '').includes('purchase'))
                .slice(0, 15)
                .map((n) => (
                  <tr key={n.id}>
                    <td>{String(n.created_at || '').slice(0, 16).replace('T', ' ')}</td>
                    <td>Compra pendiente</td>
                    <td style={{ fontSize: '0.8rem' }}>{n.body}</td>
                  </tr>
                ))}
              {!(notifications || []).some((n) => n.meta?.type === 'purchase_checkout') ? (
                <tr><td colSpan={3}>Sin compras pendientes avisadas todavía</td></tr>
              ) : null}
            </tbody>
          </table>
        </div>
      </section>

      {economy ? (
        <section>
          <form className="fl-actions" onSubmit={saveEconomy} style={{ flexDirection: 'column', alignItems: 'stretch', gap: '1rem' }}>
            <div className="fl-section-head">
              <div>
                <h2>Economía FitTokens</h2>
                <p>
                  Precio de venta → Wompi / packs. Liquidación creator =
                  {' '}
                  {Number(economy.sale_price_cop_per_token || 0).toLocaleString('es-CO')}
                  {' × '}
                  {economy.split_creator_pct || 80}
                  % =
                  {' '}
                  {Math.round(Number(economy.sale_price_cop_per_token || 0) * Number(economy.split_creator_pct || 80) / 100).toLocaleString('es-CO')}
                  {' COP/token'}
                </p>
              </div>
            </div>
            <div className="fl-kpi-grid">
              <label className="fl-kpi">
                <span>Valor venta COP / token (referencia packs)</span>
                <input
                  className="fl-field"
                  type="number"
                  min={1}
                  value={economy.sale_price_cop_per_token ?? 1000}
                  onChange={(e) => setEconomy({ ...economy, sale_price_cop_per_token: Number(e.target.value) })}
                />
              </label>
              <label className="fl-kpi">
                <span>% Creator (tu 20 % = 100 − este)</span>
                <input
                  className="fl-field"
                  type="number"
                  min={1}
                  max={99}
                  value={economy.split_creator_pct ?? 80}
                  onChange={(e) => {
                    const creator = Number(e.target.value);
                    setEconomy({
                      ...economy,
                      split_creator_pct: creator,
                      split_platform_pct: 100 - creator,
                    });
                  }}
                />
              </label>
              <label className="fl-kpi">
                <span>% Ecosistema (solo lectura)</span>
                <input
                  className="fl-field"
                  readOnly
                  value={100 - Number(economy.split_creator_pct || 80)}
                />
              </label>
              <label className="fl-kpi">
                <span>Bono bienvenida</span>
                <select
                  className="fl-field"
                  value={economy.welcome_bonus?.enabled ? '1' : '0'}
                  onChange={(e) => setEconomy({
                    ...economy,
                    welcome_bonus: {
                      ...(economy.welcome_bonus || { tokens: 10, valid_days: 8 }),
                      enabled: e.target.value === '1',
                    },
                  })}
                >
                  <option value="0">Apagado</option>
                  <option value="1">Encendido</option>
                </select>
              </label>
              <label className="fl-kpi">
                <span>Tokens bono</span>
                <input
                  className="fl-field"
                  type="number"
                  min={0}
                  value={economy.welcome_bonus?.tokens ?? 10}
                  onChange={(e) => setEconomy({
                    ...economy,
                    welcome_bonus: { ...(economy.welcome_bonus || {}), tokens: Number(e.target.value) },
                  })}
                />
              </label>
              <label className="fl-kpi">
                <span>Vigencia bono (días)</span>
                <input
                  className="fl-field"
                  type="number"
                  min={1}
                  value={economy.welcome_bonus?.valid_days ?? 8}
                  onChange={(e) => setEconomy({
                    ...economy,
                    welcome_bonus: { ...(economy.welcome_bonus || {}), valid_days: Number(e.target.value) },
                  })}
                />
              </label>
            </div>
            <div className="fl-section-head">
              <div>
                <h2>Packs de compra (Marketplace → Wompi)</h2>
                <p>Cada pack tiene tokens + precio COP que paga el atleta. Activa Wompi abajo para cobrar.</p>
              </div>
            </div>
            <div className="fl-table-wrap">
              <table className="fl-table">
                <thead>
                  <tr>
                    <th>Pack</th>
                    <th>Tokens</th>
                    <th>Bonus</th>
                    <th>Precio COP (Wompi)</th>
                  </tr>
                </thead>
                <tbody>
                  {(economy.packages || []).map((p, idx) => (
                    <tr key={p.id}>
                      <td>{p.name || p.id}</td>
                      <td>
                        <input
                          className="fl-field"
                          type="number"
                          min={1}
                          value={p.tokens}
                          onChange={(e) => {
                            const packages = [...economy.packages];
                            packages[idx] = { ...p, tokens: Number(e.target.value) };
                            setEconomy({ ...economy, packages });
                          }}
                        />
                      </td>
                      <td>
                        <input
                          className="fl-field"
                          type="number"
                          min={0}
                          value={p.bonus_tokens || 0}
                          onChange={(e) => {
                            const packages = [...economy.packages];
                            packages[idx] = { ...p, bonus_tokens: Number(e.target.value) };
                            setEconomy({ ...economy, packages });
                          }}
                        />
                      </td>
                      <td>
                        <input
                          className="fl-field"
                          type="number"
                          min={0}
                          value={p.price_cop}
                          onChange={(e) => {
                            const packages = [...economy.packages];
                            packages[idx] = { ...p, price_cop: Number(e.target.value) };
                            setEconomy({ ...economy, packages });
                          }}
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="fl-actions">
              <button type="submit" className="fl-btn fl-btn-primary" disabled={busy}>
                Guardar economía FitTokens
              </button>
            </div>
          </form>
        </section>
      ) : null}

      <section>
        <form className="fl-actions" onSubmit={saveMaster} style={{ flexDirection: 'column', alignItems: 'stretch', gap: '1rem' }}>
          <div className="fl-section-head">
            <div>
              <h2>Wompi (cobro real)</h2>
              <p>
                Permalink propio Fit Live
                {master.wompi?.cloned_from === 'training' ? ' · clonado desde Training' : ''}
                {cloneInfo?.error ? ` · ${cloneInfo.error}` : ''}
                . Secretos en .env
              </p>
            </div>
          </div>
          <div className="fl-kpi-grid">
            <label className="fl-kpi">
              <span>Marca</span>
              <input
                className="fl-field"
                value={master.brand_name || ''}
                onChange={(e) => setMaster({ ...master, brand_name: e.target.value })}
              />
            </label>
            <label className="fl-kpi">
              <span>WhatsApp soporte (botón flotante landing)</span>
              <input
                className="fl-field"
                placeholder="57300…"
                value={master.support_whatsapp || ''}
                onChange={(e) => setMaster({ ...master, support_whatsapp: e.target.value })}
              />
            </label>
            <label className="fl-kpi">
              <span>Wompi activo</span>
              <select
                className="fl-field"
                value={master.wompi?.enabled ? '1' : '0'}
                onChange={(e) => setMaster({
                  ...master,
                  wompi: { ...master.wompi, enabled: e.target.value === '1' },
                })}
              >
                <option value="0">No (atletas no pueden comprar; super_admin puede prueba)</option>
                <option value="1">Sí (checkout Wompi real)</option>
              </select>
            </label>
            <label className="fl-kpi">
              <span>Modo</span>
              <select
                className="fl-field"
                value={master.wompi?.mode || 'test'}
                onChange={(e) => setMaster({
                  ...master,
                  wompi: { ...master.wompi, mode: e.target.value },
                })}
              >
                <option value="test">Test</option>
                <option value="live">Live</option>
              </select>
            </label>
            <label className="fl-kpi" style={{ gridColumn: '1 / -1' }}>
              <span>Permalink Wompi (checkout.wompi.co/l/…)</span>
              <input
                className="fl-field"
                value={master.wompi?.permalink_url || ''}
                onChange={(e) => setMaster({
                  ...master,
                  wompi: { ...master.wompi, permalink_url: e.target.value },
                })}
                placeholder="https://checkout.wompi.co/l/…"
              />
            </label>
          </div>
          <div className="fl-actions">
            <button type="submit" className="fl-btn fl-btn-primary" disabled={busy}>
              Guardar maestro
            </button>
          </div>
        </form>
      </section>

      {settlement ? (
        <section>
          <div className="fl-section-head">
            <div>
              <h2>Liquidación preview</h2>
              <p>
                Split
                {' '}
                {settlement.split_creator_pct}
                % · valor
                {' '}
                {settlement.settlement_value_cop_per_token}
                {' COP/token'}
              </p>
            </div>
          </div>
          <div className="fl-actions" style={{ marginBottom: 10 }}>
            <button
              type="button"
              className="fl-btn fl-btn-primary"
              disabled={busy}
              onClick={async () => {
                setBusy(true);
                try {
                  const res = await fitLiveApi.payoutsAccrue();
                  setPayouts(res.payouts || []);
                  setMsg(`Payouts acumulados: ${res.created?.length || 0}`);
                } catch (err) {
                  setMsg(err.message || 'Error payouts');
                } finally {
                  setBusy(false);
                }
              }}
            >
              Generar payouts del periodo
            </button>
          </div>
          <div className="fl-table-wrap">
            <table className="fl-table">
              <thead>
                <tr>
                  <th>Creator</th>
                  <th>Nivel</th>
                  <th>Tokens</th>
                  <th>Neto COP</th>
                </tr>
              </thead>
              <tbody>
                {(settlement.creators || []).map((r) => (
                  <tr key={r.creator_id}>
                    <td>{r.name}</td>
                    <td>{r.level}</td>
                    <td>{r.tokens_attributed}</td>
                    <td>
                      $
                      {Number(r.net_cop || 0).toLocaleString('es-CO')}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {payouts.length ? (
            <div className="fl-table-wrap" style={{ marginTop: 12 }}>
              <table className="fl-table">
                <thead>
                  <tr>
                    <th>Payout</th>
                    <th>Periodo</th>
                    <th>Neto</th>
                    <th>Estado</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {payouts.map((p) => (
                    <tr key={p.id}>
                      <td>{p.creator_name}</td>
                      <td>{p.period}</td>
                      <td>
                        $
                        {Number(p.net_cop || 0).toLocaleString('es-CO')}
                      </td>
                      <td>{p.status}</td>
                      <td>
                        {p.status !== 'paid' ? (
                          <button
                            type="button"
                            className="fl-btn fl-btn-primary"
                            disabled={busy}
                            onClick={async () => {
                              setBusy(true);
                              try {
                                await fitLiveApi.payoutMarkPaid(p.id);
                                await load();
                                setMsg('Payout marcado pagado');
                              } catch (err) {
                                setMsg(err.message || 'Error');
                              } finally {
                                setBusy(false);
                              }
                            }}
                          >
                            Marcar pagado
                          </button>
                        ) : '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : null}
        </section>
      ) : null}

      {master.legal ? (
        <section>
          <div className="fl-section-head">
            <div>
              <h2>Legal mínimo (Fase 0.5)</h2>
              <p>Textos operativos antes de cobro público</p>
            </div>
          </div>
          <div className="fl-kpi-grid">
            {['tokens_terms', 'athlete_terms', 'creator_terms', 'privacy'].map((key) => (
              <label key={key} className="fl-kpi" style={{ gridColumn: '1 / -1' }}>
                <span>{key}</span>
                <textarea
                  className="fl-field"
                  rows={2}
                  value={master.legal[key] || ''}
                  onChange={(e) => setMaster({
                    ...master,
                    legal: { ...master.legal, [key]: e.target.value },
                  })}
                />
              </label>
            ))}
          </div>
        </section>
      ) : null}
    </>
  );
}

function ControlTower({ snapshot, setBusy, setMsg, busy }) {
  const [replayReport, setReplayReport] = useState(null);
  const [replayCreators, setReplayCreators] = useState([]);
  const [replayCreatorId, setReplayCreatorId] = useState('');
  const [openReplayId, setOpenReplayId] = useState(null);

  const loadReplays = useCallback(async (creatorId = '') => {
    try {
      const res = await fitLiveApi.masterReplays(creatorId || undefined);
      setReplayReport(res);
      if (!creatorId && Array.isArray(res?.by_creator)) setReplayCreators(res.by_creator);
    } catch (err) {
      setMsg(err.message || 'No se pudo cargar grabaciones');
    }
  }, [setMsg]);

  useEffect(() => {
    loadReplays('');
  }, [loadReplays]);

  const tower = snapshot?.control_tower;

  const replayPanel = (
    <section>
      <div className="fl-section-head">
        <div>
          <h2>Grabaciones y vistas</h2>
          <p>Replays por entrenador · quién ingresó a verlos · tokens cobrados</p>
        </div>
        <button
          type="button"
          className="fl-btn"
          disabled={busy}
          onClick={() => loadReplays(replayCreatorId)}
        >
          Actualizar
        </button>
      </div>
      <div className="fl-actions" style={{ marginBottom: 12, flexWrap: 'wrap' }}>
        <label className="fl-kpi" style={{ minWidth: 220 }}>
          <span>Filtrar entrenador</span>
          <select
            className="fl-field"
            value={replayCreatorId}
            onChange={(e) => {
              const id = e.target.value;
              setReplayCreatorId(id);
              setOpenReplayId(null);
              loadReplays(id);
            }}
          >
            <option value="">Todos</option>
            {replayCreators.map((c) => (
              <option key={c.creator_id} value={c.creator_id}>
                {c.creator_name}
                {' · '}
                {c.views}
                {' vistas'}
              </option>
            ))}
          </select>
        </label>
      </div>
      {replayReport?.summary ? (
        <div className="fl-kpi-grid" style={{ marginBottom: 12 }}>
          <div className="fl-kpi"><label>Grabaciones</label><strong>{replayReport.summary.recordings}</strong></div>
          <div className="fl-kpi"><label>Vistas</label><strong>{replayReport.summary.views}</strong></div>
          <div className="fl-kpi"><label>Tokens cobrados</label><strong>{replayReport.summary.tokens_consumed}</strong></div>
          <div className="fl-kpi"><label>Entrenadores</label><strong>{replayReport.summary.creators}</strong></div>
        </div>
      ) : (
        <p className="fl-note">Cargando grabaciones…</p>
      )}
      <div className="fl-table-wrap">
        <table className="fl-table">
          <thead>
            <tr>
              <th>Grabación</th>
              <th>Entrenador</th>
              <th>Estado</th>
              <th>Vistas</th>
              <th>Tokens</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {(replayReport?.recordings || []).flatMap((r) => {
              const main = (
                <tr key={r.class_id}>
                  <td>{r.title}</td>
                  <td>{r.creator_name}</td>
                  <td>{r.status === 'published' ? 'Publicada' : r.status}</td>
                  <td>{r.views_count}</td>
                  <td>{r.tokens_consumed}</td>
                  <td>
                    <button
                      type="button"
                      className="fl-btn"
                      onClick={() => setOpenReplayId(openReplayId === r.class_id ? null : r.class_id)}
                    >
                      {openReplayId === r.class_id ? 'Ocultar' : 'Quién ingresó'}
                    </button>
                  </td>
                </tr>
              );
              if (openReplayId !== r.class_id) return [main];
              return [
                main,
                <tr key={`${r.class_id}-detail`}>
                  <td colSpan={6}>
                    {r.viewers?.length ? (
                      <table className="fl-table" style={{ margin: 0 }}>
                        <thead>
                          <tr>
                            <th>Usuario</th>
                            <th>Email</th>
                            <th>Tokens</th>
                            <th>Fecha ingreso</th>
                          </tr>
                        </thead>
                        <tbody>
                          {r.viewers.map((v) => (
                            <tr key={v.id}>
                              <td>{v.user_nombre || `User ${v.user_id}`}</td>
                              <td>{v.user_email || '—'}</td>
                              <td>{v.tokens}</td>
                              <td>{String(v.created_at || '').replace('T', ' ').slice(0, 19)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    ) : (
                      <p className="fl-note" style={{ margin: 0 }}>Nadie ha visto esta grabación aún.</p>
                    )}
                  </td>
                </tr>,
              ];
            })}
            {replayReport && !(replayReport.recordings || []).length ? (
              <tr><td colSpan={6}>Sin grabaciones (replays) todavía</td></tr>
            ) : null}
          </tbody>
        </table>
      </div>
    </section>
  );

  if (!tower) {
    return (
      <>
        <section>
          <div className="fl-section-head">
            <div>
              <h2>Control Tower</h2>
              <p>Sin datos de métricas aún (API / snapshot vacío).</p>
            </div>
          </div>
        </section>
        {replayPanel}
      </>
    );
  }
  const rows = (tower.by_creator || []).map((r) => ({
    name: r.name,
    tokens: r.tokens_consumed ?? 0,
    attendance: r.attendance_rate ?? 0,
    occupancy: r.occupancy_rate ?? 0,
    reservations: r.reservations ?? 0,
    published: r.classes_published ?? 0,
  }));

  const kpi = {
    creators_active: tower.creators_active ?? 0,
    classes_published: tower.classes_published ?? 0,
    tokens_in_circulation: tower.tokens_in_circulation ?? 0,
    tokens_sold: tower.tokens_sold ?? 0,
    tokens_bonus: tower.tokens_bonus ?? 0,
    attendance_rate: tower.attendance_rate ?? 0,
    tokens_consumed: tower.tokens_consumed ?? 0,
    occupancy_rate: tower.occupancy_rate ?? 0,
    tokens_refunded: tower.tokens_refunded ?? 0,
    rating_avg: tower.rating_avg ?? 0,
    classes_draft: tower.classes_draft ?? 0,
    split_creator_pct: tower.split_creator_pct ?? 80,
    split_platform_pct: tower.split_platform_pct ?? 20,
    drift_flag: tower.drift_flag ?? false,
    reservations: tower.reservations ?? 0,
    replays_views: tower.replays_views ?? 0,
  };

  const beta = tower.beta;
  const attendanceRows = tower.attendance?.by_class || [];

  const downloadCsv = async () => {
    setBusy(true);
    try {
      const csv = await fitLiveApi.attendanceCsv();
      const blob = new Blob([csv], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'fit-live-asistencia.csv';
      a.click();
      URL.revokeObjectURL(url);
      setMsg('CSV de asistencia descargado');
    } catch (err) {
      setMsg(err.message || 'Error CSV');
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <section>
        <div className="fl-section-head">
          <div>
            <h2>Control Tower</h2>
            <p>Asistencia · tokens · beta readiness · eventos KPI</p>
          </div>
          <span className="fl-banner-phase">{snapshot?.phase ? `Fase ${snapshot.phase}` : 'Live'}</span>
        </div>
        <div className="fl-kpi-grid">
          <div className="fl-kpi"><label>Creators</label><strong>{kpi.creators_active}</strong></div>
          <div className="fl-kpi"><label>Publicadas</label><strong>{kpi.classes_published}</strong></div>
          <div className="fl-kpi"><label>Borradores</label><strong>{kpi.classes_draft ?? 0}</strong></div>
          <div className="fl-kpi"><label>Tokens circulación</label><strong>{kpi.tokens_in_circulation}</strong></div>
          <div className="fl-kpi"><label>Tokens vendidos</label><strong>{kpi.tokens_sold ?? 0}</strong></div>
          <div className="fl-kpi"><label>Bonos</label><strong>{kpi.tokens_bonus ?? 0}</strong></div>
          <div className="fl-kpi"><label>Split Creator</label><strong>{kpi.split_creator_pct ?? 70}%</strong></div>
          <div className="fl-kpi"><label>Split Plataforma</label><strong>{kpi.split_platform_pct ?? 30}%</strong></div>
          <div className="fl-kpi"><label>Drift ledger</label><strong>{kpi.drift_flag ? '⚠' : 'OK'}</strong></div>
          <div className="fl-kpi"><label>Reservas</label><strong>{kpi.reservations ?? 0}</strong></div>
          <div className="fl-kpi"><label>Asistencia</label><strong>{pct(kpi.attendance_rate)}</strong></div>
          <div className="fl-kpi"><label>Replays</label><strong>{kpi.replays_views ?? 0}</strong></div>
        </div>
      </section>

      {beta ? (
        <section>
          <div className="fl-section-head">
            <div>
              <h2>Beta / go-to-market</h2>
              <p>
                Decisión:
                {' '}
                <strong>{beta.decision === 'open_public' ? 'LISTO para abrir' : 'Iterar'}</strong>
              </p>
            </div>
            <span className="fl-banner-phase">{beta.ready ? 'OK' : 'Pendiente'}</span>
          </div>
          <div className="fl-kpi-grid">
            {Object.entries(beta.checks || {}).map(([key, c]) => (
              <div key={key} className="fl-kpi">
                <label>{key}</label>
                <strong>{typeof c.value === 'number' && c.value < 2 ? c.value.toFixed(2) : c.value}</strong>
                <small>{c.ok ? 'OK' : 'Falta'}</small>
              </div>
            ))}
          </div>
        </section>
      ) : null}

      <section>
        <div className="fl-section-head">
          <div>
            <h2>Asistencia operativa</h2>
            <p>Por clase · export CSV</p>
          </div>
          <button type="button" className="fl-btn fl-btn-primary" disabled={busy} onClick={downloadCsv}>
            Export CSV
          </button>
        </div>
        <div className="fl-table-wrap">
          <table className="fl-table">
            <thead>
              <tr>
                <th>Clase</th>
                <th>Creator</th>
                <th>Res</th>
                <th>Presentes</th>
                <th>No-show</th>
                <th>Asist.%</th>
              </tr>
            </thead>
            <tbody>
              {attendanceRows.slice(0, 40).map((r) => (
                <tr key={r.class_id}>
                  <td>{r.title}</td>
                  <td>{r.creator_name}</td>
                  <td>{r.reserved}</td>
                  <td>{r.present}</td>
                  <td>{r.noshow}</td>
                  <td>{pct(r.attendance_rate)}</td>
                </tr>
              ))}
              {attendanceRows.length === 0 ? (
                <tr><td colSpan={6}>Sin clases publicadas aún</td></tr>
              ) : null}
            </tbody>
          </table>
        </div>
      </section>

      {replayPanel}

      {snapshot?.economy ? (
        <section>
          <div className="fl-section-head">
            <div>
              <h2>Economía del token</h2>
              <p>
                Venta
                {' '}
                {snapshot.economy.sale_price_cop_per_token}
                {' COP · liquidación '}
                {snapshot.economy.settlement_value_cop_per_token}
                {' COP'}
              </p>
            </div>
          </div>
          <div className="fl-actions">
            {(snapshot.economy.packages || []).map((p) => (
              <div key={p.id} className="fl-btn">
                {p.name}
                {' · '}
                {p.tokens}
                tk · $
                {Number(p.price_cop).toLocaleString('es-CO')}
              </div>
            ))}
          </div>
        </section>
      ) : null}

      <section>
        <div className="fl-section-head">
          <div>
            <h2>Por coach</h2>
            <p>Publicadas ahora · tokens/asistencia cuando existan</p>
          </div>
        </div>
        <div className="fl-table-wrap">
          <table className="fl-table">
            <thead>
              <tr>
                <th>Coach</th>
                <th>Publicadas</th>
                <th>Tokens</th>
                <th>Asistencia</th>
                <th>Reservas</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.name}>
                  <td>{r.name}</td>
                  <td>{r.published}</td>
                  <td>{r.tokens}</td>
                  <td>{typeof r.attendance === 'number' ? pct(r.attendance) : r.attendance}</td>
                  <td>{r.reservations}</td>
                </tr>
              ))}
              {rows.length === 0 ? (
                <tr><td colSpan={5}>Sin creators con KPI aún</td></tr>
              ) : null}
            </tbody>
          </table>
        </div>
      </section>
    </>
  );
}

function isFitLiveSuperAdmin(user) {
  const roles = [user?.rol, ...(Array.isArray(user?.roles) ? user.roles : [])].filter(Boolean);
  return roles.includes('super_admin');
}

export default function FitLiveApp({ user, onBack, canManageStudio: canManageStudioProp = false }) {
  const isPlatformAdmin = isFitLiveSuperAdmin(user);
  const [studioFromApi, setStudioFromApi] = useState(false);
  const canManageStudio = canManageStudioProp || studioFromApi;
  /** Creator (no super_admin): solo Studio / su catálogo — sin Marketplace de otros. */
  const isCreatorOnly = canManageStudio && !isPlatformAdmin;
  const [tab, setTab] = useState(() => (
    (canManageStudioProp && !isFitLiveSuperAdmin(user)) ? 'studio' : 'athlete'
  ));
  const [trainerId, setTrainerId] = useState('');
  const [directory, setDirectory] = useState({ creators: [], supportWhatsapp: '' });
  const [snapshot, setSnapshot] = useState(null);
  const [published, setPublished] = useState([]);
  const [mine, setMine] = useState([]);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');
  const [showImport, setShowImport] = useState(false);
  const [wallet, setWallet] = useState(null);
  const [packages, setPackages] = useState([]);
  const [reservations, setReservations] = useState([]);
  const [wompi, setWompi] = useState(null);
  /** Confirmación antes de descontar tokens (reserva o replay). */
  const [spendConfirm, setSpendConfirm] = useState(null);

  useEffect(() => {
    if (isCreatorOnly) {
      if (tab !== 'studio') setTab('studio');
      return;
    }
    if (!isPlatformAdmin && (tab === 'tower' || tab === 'maestro' || tab === 'users')) {
      setTab(canManageStudio ? 'studio' : 'athlete');
    }
  }, [isPlatformAdmin, isCreatorOnly, tab, canManageStudio]);

  const refresh = useCallback(async () => {
    try {
      const creatorOnly = canManageStudio && !isPlatformAdmin;
      const [snap, pub, w, packs, mineResv, dir] = await Promise.all([
        isPlatformAdmin ? fitLiveApi.snapshot().catch(() => null) : Promise.resolve(null),
        creatorOnly ? Promise.resolve({ classes: [] }) : fitLiveApi.listPublished().catch(() => ({ classes: [] })),
        fitLiveApi.walletMe().catch(() => null),
        creatorOnly ? Promise.resolve({ packages: [], wompi: null }) : fitLiveApi.packages().catch(() => ({ packages: [] })),
        creatorOnly ? Promise.resolve({ reservations: [] }) : fitLiveApi.myReservations().catch(() => ({ reservations: [] })),
        creatorOnly ? Promise.resolve(null) : fetchPublicDirectory().catch(() => null),
      ]);
      if (snap) setSnapshot(snap);
      if (dir) setDirectory({ creators: dir.creators, supportWhatsapp: dir.supportWhatsapp });
      else if (creatorOnly) setDirectory({ creators: [], supportWhatsapp: '' });
      setPublished(pub.classes || []);
      if (w?.wallet) setWallet(w.wallet);
      setPackages(packs.packages || []);
      setWompi(packs.wompi || null);
      setReservations(mineResv.reservations || []);
      if (canManageStudio) {
        const mineRes = await fitLiveApi.listMine().catch(() => ({ classes: [] }));
        setMine(mineRes.classes || []);
      } else {
        setMine([]);
      }
    } catch {
      /* API offline — UI vacía, sin mocks de negocio */
    }
  }, [canManageStudio, isPlatformAdmin]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  useEffect(() => {
    if (canManageStudioProp) return;
    fitLiveApi.me()
      .then((r) => {
        const ok = r?.can_manage_studio === true;
        setStudioFromApi(ok);
        if (ok && !isFitLiveSuperAdmin(user)) setTab('studio');
      })
      .catch(() => {});
  }, [canManageStudioProp, user]);

  const onBuy = async (packId) => {
    setBusy(true);
    setMsg('');
    try {
      if (wompi?.enabled && wompi?.permalink_url) {
        const co = await fitLiveApi.checkoutWompi(packId);
        const packLabel = co.pack
          ? `${co.pack.name || packId}: ${co.pack.total_tokens} tokens ($${Number(co.pack.price_cop || 0).toLocaleString('es-CO')})`
          : packId;
        setMsg(
          `Compra iniciada · ${packLabel}. El admin de Fit Live recibió aviso para verificar el pago y acreditar tus tokens (Usuarios → Vigencias). Ref ${co.purchase?.reference || ''}`,
        );
        if (co.checkout_url) window.open(co.checkout_url, '_blank', 'noopener,noreferrer');
        window.scrollTo({ top: 0, behavior: 'smooth' });
        return;
      }
      if (!isPlatformAdmin) {
        setMsg('Compra no disponible: falta Wompi en Maestro Fit Live');
        return;
      }
      const res = await fitLiveApi.purchaseTest(packId);
      setWallet(res.wallet);
      setMsg(`Compra prueba OK · saldo ${res.wallet.balance}`);
      const snap = await fitLiveApi.snapshot().catch(() => null);
      if (snap) setSnapshot(snap);
    } catch (err) {
      setMsg(err.message || 'Error compra');
    } finally {
      setBusy(false);
    }
  };

  const runWatchReplay = async (item) => {
    setBusy(true);
    setMsg('');
    try {
      const res = await fitLiveApi.watchReplay(item.id);
      if (res.wallet) setWallet(res.wallet);
      const url = String(res.view?.video_url || '').trim();
      const spent = Number(res.view?.tokens || 0);
      if (url) {
        const opened = window.open(url, '_blank', 'noopener,noreferrer');
        setMsg(
          opened
            ? (res.deduped
              ? 'Replay abierto (ya lo tenías desbloqueado).'
              : (spent > 0 ? `Replay abierto · -${spent} tokens` : 'Replay gratis abierto.'))
            : `El navegador bloqueó la ventana. Abre el video aquí: ${url}`,
        );
      } else {
        setMsg('Replay desbloqueado, pero no hay URL de video. Pide al creator que la cargue.');
      }
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (err) {
      const code = err.payload?.error || err.message;
      setMsg(fitLiveUserMsg(code, err.message || 'Error al ver replay'));
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } finally {
      setBusy(false);
    }
  };

  const askSpendConfirm = (item, kind) => {
    if (!item?.id) return;
    setSpendConfirm({
      kind,
      item,
      tokens: classSpendTokens(item),
      title: kind === 'replay' ? '¿Ver clase grabada?' : '¿Reservar esta clase?',
    });
  };

  const onReserve = (item) => askSpendConfirm(item, 'reserve');
  const onWatchReplay = (item) => askSpendConfirm(item, 'replay');

  const onSpendConfirmOk = async () => {
    const pending = spendConfirm;
    if (!pending?.item) return;
    setSpendConfirm(null);
    if (pending.kind === 'replay') {
      await runWatchReplay(pending.item);
      return;
    }
    await reserveClass(pending.item, {
      setBusy, setMsg, setWallet, refresh,
    });
  };

  const onCancel = async (r) => {
    setBusy(true);
    setMsg('');
    try {
      await fitLiveApi.cancelReservation(r.id);
      setMsg('Reserva cancelada · tokens liberados');
      await refresh();
    } catch (err) {
      setMsg(err.message || 'Error al cancelar');
    } finally {
      setBusy(false);
    }
  };

  const onRate = async (r) => {
    setBusy(true);
    setMsg('');
    try {
      await fitLiveApi.rate(r.class_id, 5, '');
      setMsg('Gracias por tu valoración');
    } catch (err) {
      setMsg(err.message || 'Error al valorar');
    } finally {
      setBusy(false);
    }
  };

  const onTip = async (r) => {
    setBusy(true);
    setMsg('');
    try {
      const res = await fitLiveApi.tip(r.class_id, 2, 'tip');
      if (res.wallet) setWallet(res.wallet);
      setMsg('Tip enviado · 2 tokens');
      await refresh();
    } catch (err) {
      setMsg(err.message === 'insufficient_tokens' ? 'Saldo insuficiente' : (err.message || 'Error tip'));
    } finally {
      setBusy(false);
    }
  };

  const onOpenZoom = async (r) => {
    if (!r?.zoom_url) {
      setMsg('Esta clase aún no tiene enlace Zoom');
      return;
    }
    window.open(r.zoom_url, '_blank', 'noopener,noreferrer');
    if (!['held', 'confirmed'].includes(r.status)) return;
    setBusy(true);
    setMsg('');
    try {
      await fitLiveApi.attend(r.id, true);
      setMsg('Entraste a Zoom · asistencia registrada');
      await refresh();
    } catch (err) {
      const code = err.message || '';
      if (code === 'checkin_not_open' || code === 'checkin_closed') {
        setMsg('Zoom abierto. La asistencia se registra cerca del horario de la clase.');
      } else {
        setMsg(code || 'Zoom abierto; no se pudo registrar asistencia');
      }
    } finally {
      setBusy(false);
    }
  };

  const greet = user?.nombre ? user.nombre.split(' ')[0] : 'Atleta';
  const supportHref = waUrl(directory.supportWhatsapp, 'Hola, necesito ayuda con Fit Live');

  return (
    <div className="fit-live-root" data-testid="fit-live-root">
      <FitLiveSpendConfirmModal
        open={Boolean(spendConfirm)}
        title={spendConfirm?.title || 'Confirmar'}
        classTitle={spendConfirm?.item?.title || ''}
        tokens={spendConfirm?.tokens || 0}
        balance={wallet?.balance ?? 0}
        busy={busy}
        onCancel={() => !busy && setSpendConfirm(null)}
        onConfirm={onSpendConfirmOk}
      />
      <div className="fl-shell">
        <header className="fl-topbar">
          <div className="fl-brand">
            <strong>Fit Live</strong>
            <span>
              Hola
              {' '}
              {greet}
              {' · '}
              {wallet != null ? `${wallet.balance} tokens` : 'Ecosistema Fit'}
            </span>
          </div>
          <nav className="fl-tabs" aria-label="Fit Live">
            {!isCreatorOnly ? (
              <button type="button" className={`fl-tab${tab === 'athlete' ? ' is-active' : ''}`} onClick={() => setTab('athlete')}>
                Marketplace
              </button>
            ) : null}
            {isPlatformAdmin ? (
              <button type="button" className={`fl-tab${tab === 'users' ? ' is-active' : ''}`} onClick={() => setTab('users')}>
                Usuarios
              </button>
            ) : null}
            {canManageStudio ? (
              <button type="button" className={`fl-tab${tab === 'studio' ? ' is-active' : ''}`} onClick={() => setTab('studio')}>
                Creator Studio
              </button>
            ) : null}
            {isPlatformAdmin ? (
              <>
                <button type="button" className={`fl-tab${tab === 'tower' ? ' is-active' : ''}`} onClick={() => setTab('tower')}>
                  Control Tower
                </button>
                <button type="button" className={`fl-tab${tab === 'maestro' ? ' is-active' : ''}`} onClick={() => setTab('maestro')}>
                  Maestro
                </button>
              </>
            ) : null}
          </nav>
          <button type="button" className="fl-back" onClick={onBack}>
            Volver al ecosistema
          </button>
        </header>
        {msg ? (
          <p
            className={`fl-banner-msg${/sin tokens|error|falta|bloqueó|no disponible/i.test(msg) ? ' fl-banner-msg--warn' : ''}`}
            role="status"
          >
            {msg}
          </p>
        ) : null}
        <div className="fl-body">
          {!isCreatorOnly && tab === 'athlete' ? (
            <AthleteHome
              trainerId={trainerId}
              setTrainerId={setTrainerId}
              creators={directory.creators}
              published={published}
              wallet={wallet}
              packages={packages}
              onBuy={onBuy}
              busy={busy}
              wompi={wompi}
              allowTestPurchase={isPlatformAdmin}
              onReserve={onReserve}
              onWatchReplay={onWatchReplay}
              reservations={reservations}
              onCancel={onCancel}
              onRate={onRate}
              onTip={onTip}
              onOpenZoom={onOpenZoom}
            />
          ) : null}
          {isPlatformAdmin && tab === 'users' ? (
            <div className="fl-admin-island ui-light-panel surface-readable d28d-admin-surface p-4 rounded-2xl bg-white/90 border border-stone-200">
              <FitLiveUsersAdmin />
            </div>
          ) : null}
          {canManageStudio && tab === 'studio' ? (
            <CreatorStudio
              mine={mine}
              onRefresh={refresh}
              busy={busy}
              setBusy={setBusy}
              setMsg={setMsg}
              showImport={showImport}
              setShowImport={setShowImport}
              isPlatformAdmin={isPlatformAdmin}
            />
          ) : null}
          {isPlatformAdmin && tab === 'tower' ? (
            <ControlTower snapshot={snapshot} setBusy={setBusy} setMsg={setMsg} busy={busy} />
          ) : null}
          {isPlatformAdmin && tab === 'maestro' ? (
            <MaestroEcosistema busy={busy} setBusy={setBusy} setMsg={setMsg} />
          ) : null}
        </div>
      </div>
      {supportHref ? (
        <a className="fl-fab-wa" href={supportHref} target="_blank" rel="noreferrer" title="WhatsApp soporte">
          WhatsApp
        </a>
      ) : null}
    </div>
  );
}
